import React, { Fragment } from 'react';
import { 
  Clock, 
  Users, 
  Mic, 
  MessageSquare, 
  Flag, 
  Award,
  FileText,
  Gavel,
  Globe,
  Building,
  Scale,
  Pause,
  PlayCircle,
  X
} from 'lucide-react';
import ModelCongressLayout from './ModelCongressLayout';

const DebateLayout = ({ 
  format, 
  topic, 
  stage, 
  stageCode,
  activeSpeaker,
  duration,
  stageType,
  timer, 
  log, 
  message, 
  setMessage, 
  sendTurn, 
  loading, 
  error,
  userSide,
  botSide,
  frameworks,
  parliamentaryMotions = [],
  parliamentaryPoints = [],
  onProposeMotion,
  onRaisePoint,
  selectedCommittee,
  selectedBill,
  delegates = [],
  isPaused = false,
  onPauseToggle,
  onEndDebate
}) => {
  
  const formatConfig = {
    LD: {
      icon: <Scale className="w-6 h-6" />,
      title: "Lincoln-Douglas Debate",
      description: "1 vs. 1, values-based, philosophical debate",
      color: "blue",
      participants: ["Affirmative", "Negative"],
      showParliamentary: false
    },
    PF: {
      icon: <Building className="w-6 h-6" />,
      title: "Public Forum Debate", 
      description: "2 vs. 2, accessible, policy-driven debate",
      color: "green",
      participants: ["Team A", "Team B"],
      showParliamentary: false
    },
    Congress: {
      icon: <Gavel className="w-6 h-6" />,
      title: "Congressional Debate",
      description: "Chamber simulation of U.S. Congress",
      color: "purple",
      participants: ["Speaker", "Representatives"],
      showParliamentary: true
    },
    MUN: {
      icon: <Globe className="w-6 h-6" />,
      title: "Model United Nations",
      description: "UN simulation, diplomacy and coalition-building",
      color: "indigo",
      participants: ["Delegates", "Chair"],
      showParliamentary: true
    },
    ModelCongress: {
      icon: <FileText className="w-6 h-6" />,
      title: "Model Congress",
      description: "U.S. legislative simulation with committees",
      color: "red",
      participants: ["Representatives", "Committee Members"],
      showParliamentary: true
    }
  };

  const config = formatConfig[format] || formatConfig.LD;

  // Use ModelCongressLayout for ModelCongress format
  if (format === 'ModelCongress') {
    return (
      <ModelCongressLayout
        format={format}
        topic={topic}
        stage={stage}
        stageCode={stageCode}
        activeSpeaker={activeSpeaker}
        duration={duration}
        stageType={stageType}
        timer={timer}
        log={log}
        message={message}
        setMessage={setMessage}
        sendTurn={sendTurn}
        loading={loading}
        error={error}
            selectedBill={selectedBill}
            selectedCommittee={selectedCommittee}
            delegates={delegates}
            isPaused={isPaused}
        onPauseToggle={onPauseToggle}
        onEndDebate={onEndDebate}
        onProposeMotion={onProposeMotion}
        onRaisePoint={onRaisePoint}
      />
    );
  }

  const colorClasses = {
    blue: "border-blue-500 bg-blue-50 dark:bg-blue-900/20",
    green: "border-green-500 bg-green-50 dark:bg-green-900/20", 
    purple: "border-purple-500 bg-purple-50 dark:bg-purple-900/20",
    indigo: "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20",
    red: "border-red-500 bg-red-50 dark:bg-red-900/20"
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // For LD: Determine if it's the user's turn
  const isUserTurn = format === 'LD' && activeSpeaker ? 
    (userSide === 'PRO' ? activeSpeaker === 'Affirmative' : activeSpeaker === 'Negative') ||
    (stageType === 'cross-examination' && (
      (userSide === 'PRO' && stageCode === 'CX2') || 
      (userSide === 'CON' && stageCode === 'CX1')
    ))
    : true; // Default to true for other formats

  // For LD: Get stage duration from prop or calculate from stage code
  const stageDuration = duration || (format === 'LD' && stageCode ? 
    (stageCode === '1AC' ? 360 : 
     stageCode === 'CX1' ? 180 :
     stageCode === '1NC' ? 420 :
     stageCode === 'CX2' ? 180 :
     stageCode === '1AR' ? 240 :
     stageCode === '2NR' ? 360 :
     stageCode === '2AR' ? 180 : 180) : null);

  return (
    <div className="bg-white dark:bg-gray-800 relative flex flex-col h-full w-full">
      {/* Pause Overlay */}
      {isPaused && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 flex flex-col items-center gap-4">
            <Pause className="w-16 h-16 text-yellow-600 dark:text-yellow-500" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Debate Paused</h3>
            <p className="text-gray-600 dark:text-gray-400">The debate timer has been paused. Click "Resume Debate" to continue.</p>
          </div>
        </div>
      )}
      {/* Compact Header Section - Single Row */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
          {/* Left: Resolution */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Resolution</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{topic}</span>
          </div>
          
          {/* Center: Title and Description */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <div className={`${config.color === 'blue' ? 'text-blue-600 dark:text-blue-400' : config.color === 'green' ? 'text-green-600 dark:text-green-400' : config.color === 'purple' ? 'text-purple-600 dark:text-purple-400' : config.color === 'indigo' ? 'text-indigo-600 dark:text-indigo-400' : 'text-red-600 dark:text-red-400'}`}>
              {React.cloneElement(config.icon, { className: 'w-4 h-4' })}
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">AI Debate - {config.title}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{config.description}</div>
            </div>
          </div>
          
          {/* Right: Timer and Buttons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {duration && (
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Time</div>
                <div className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatTime(timer)} /{formatTime(duration)}</div>
              </div>
            )}
            {onPauseToggle && (
              <button
                onClick={onPauseToggle}
                className="flex items-center gap-1.5 bg-yellow-600 hover:bg-yellow-700 text-white px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
              >
                {isPaused ? (
                  <>
                    <PlayCircle className="w-3.5 h-3.5" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="w-3.5 h-3.5" />
                    Pause Debate
                  </>
                )}
              </button>
            )}
            {onEndDebate && (
              <button
                onClick={onEndDebate}
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
              >
                <X className="w-3.5 h-3.5" />
                End Debate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* LD-Specific: Framework Display */}
      {format === 'LD' && frameworks && (
        <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-700">
          <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Frameworks</h4>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white dark:bg-gray-800 p-2 rounded">
              <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">Affirmative</div>
              {frameworks.affirmative?.value ? (
                <>
                  <div className="text-xs font-medium text-gray-900 dark:text-white">Value: {frameworks.affirmative.value}</div>
                  <div className="text-[10px] text-gray-600 dark:text-gray-400">Criterion: {frameworks.affirmative.criterion || 'Not established'}</div>
                </>
              ) : (
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Not yet established</div>
              )}
            </div>
            <div className="bg-white dark:bg-gray-800 p-2 rounded">
              <div className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1">Negative</div>
              {frameworks.negative?.value ? (
                <>
                  <div className="text-xs font-medium text-gray-900 dark:text-white">Value: {frameworks.negative.value}</div>
                  <div className="text-[10px] text-gray-600 dark:text-gray-400">Criterion: {frameworks.negative.criterion || 'Not established'}</div>
                </>
              ) : (
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Not yet established</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LD-Specific: Stage Progression Timeline */}
      {format === 'LD' && (
        <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between text-xs overflow-x-auto">
            {['1AC', 'CX1', '1NC', 'CX2', '1AR', '2NR', '2AR'].map((code, index) => {
              const isCurrent = stageCode === code;
              const isPast = stageCode && ['1AC', 'CX1', '1NC', 'CX2', '1AR', '2NR', '2AR'].indexOf(stageCode) > index;
              return (
                <Fragment key={code}>
                  <div className="flex flex-col items-center min-w-[50px]">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isCurrent 
                        ? 'bg-primary-600 text-white ring-1 ring-primary-300' 
                        : isPast 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                      {code}
                    </div>
                    <div className={`mt-1 text-xs text-center ${isCurrent ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {code === '1AC' ? '6m' : code === 'CX1' ? '3m' : code === '1NC' ? '7m' : code === 'CX2' ? '3m' : code === '1AR' ? '4m' : code === '2NR' ? '6m' : '3m'}
                    </div>
                  </div>
                  {index < 6 && (
                    <div className={`flex-1 h-0.5 mx-1 min-w-[20px] ${
                      isPast ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* LD-Specific: Turn Indicator */}
      {format === 'LD' && (
        <div className={`border-b border-gray-200 dark:border-gray-700 px-3 py-1.5 ${
          isUserTurn ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isUserTurn ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {isUserTurn ? 'Your Turn' : `Waiting for ${activeSpeaker || 'Opponent'}`}
              </span>
            </div>
            {stageType && (
              <span className="text-xs text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded capitalize">
                {stageType.replace('-', ' ')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Format-Specific Information */}
      {format === 'ModelCongress' && selectedCommittee && (
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Committee: {selectedCommittee}</h4>
              {selectedBill && (
                <p className="text-sm text-gray-600 dark:text-gray-300">Bill: {selectedBill.title}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">{config.participants.join(' • ')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Debate Log */}
      <div className="p-4 flex-1 overflow-y-auto min-h-0">
        {log.map((entry, index) => (
          <div key={index} className={`mb-4 p-3 rounded-lg ${
            entry.role === 'user' 
              ? 'bg-blue-100 dark:bg-blue-900/30 ml-8' 
              : entry.role === 'bot' 
              ? 'bg-gray-100 dark:bg-gray-700 mr-8' 
              : 'bg-yellow-100 dark:bg-yellow-900/30 text-center'
          }`}>
            <div className="flex items-start">
              {entry.role === 'user' && <Mic className="w-5 h-5 mr-2 mt-0.5 text-blue-600" />}
              {entry.role === 'bot' && <MessageSquare className="w-5 h-5 mr-2 mt-0.5 text-gray-600" />}
              <div className="flex-1">
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{entry.text}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Parliamentary Procedures - Only for applicable formats */}
      {config.showParliamentary && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Parliamentary Procedures</h4>
          <div className="flex space-x-2">
            <button
              onClick={() => onProposeMotion && onProposeMotion()}
              className="flex items-center px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
            >
              <Flag className="w-3 h-3 mr-1" />
              Propose Motion
            </button>
            <button
              onClick={() => onRaisePoint && onRaisePoint()}
              className="flex items-center px-3 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800"
            >
              <Award className="w-3 h-3 mr-1" />
              Raise Point
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        {format === 'LD' && !isUserTurn && (
          <div className="mb-3 p-2 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-400 text-yellow-700 dark:text-yellow-300 rounded text-sm">
            ⏸️ It is not your turn. Please wait for {activeSpeaker || 'your opponent'} to speak.
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && isUserTurn && sendTurn()}
            placeholder={
              format === 'LD' && !isUserTurn 
                ? `Waiting for ${activeSpeaker || 'opponent'}...` 
                : format === 'ModelCongress' 
                ? 'Type your speech...' 
                : format === 'LD' && stageType === 'cross-examination'
                ? 'Ask a question or answer...'
                : 'Type your response...'
            }
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
            disabled={loading || (format === 'LD' && !isUserTurn)}
          />
          <button
            onClick={sendTurn}
            disabled={loading || !message.trim() || (format === 'LD' && !isUserTurn)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <Mic className="w-4 h-4 mr-1" />
                {format === 'ModelCongress' ? 'Speak' : format === 'LD' && stageType === 'cross-examination' ? 'Ask/Answer' : 'Send'}
              </>
            )}
          </button>
        </div>
        {format === 'LD' && stageType === 'rebuttal' && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            ⚠️ Rebuttal stage: No new arguments allowed. Only refutation, extensions, and weighing.
          </div>
        )}
      </div>
    </div>
  );
};

export default DebateLayout;
